import React from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import BusinessIcon from "@mui/icons-material/Business";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import DevicesIcon from "@mui/icons-material/Devices";
import CloudIcon from "@mui/icons-material/Cloud";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import SummarizeIcon from "@mui/icons-material/Summarize";
import DownloadIcon from "@mui/icons-material/Download";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import WifiIcon from "@mui/icons-material/Wifi";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import LockIcon from "@mui/icons-material/Lock";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import { useAuth } from "../providers/AuthProvider";
import TopBar from "./TopBar";
import Footer from "./Footer";
import { useEffect, useState } from "react";
import AgentDownloadDialog from "./AgentDownloadDialog";
import AppTour from "./AppTour";
import { EnterprisesAPI } from "../api/enterprises";
import { listClouds } from "../api/clouds";
import { ROUTES } from "../routes";
import { OnboardingAPI } from '../api/onboarding';
import OnboardingDialog from './OnboardingDialog';

const drawerWidth = 260;

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const sidebarWidth = !isMobile && sidebarCollapsed ? 76 : drawerWidth;
  
  // Proper role detection
  const userRole = auth?.user?.role;
  const pageAccess = Array.isArray(auth?.user?.page_access) ? auth.user.page_access : [];
  const isSuperAdmin = userRole === 'super' || userRole === 'superadmin';
  const isEnterpriseAdmin = userRole === 'enterprise_admin';
  const isPartner = userRole === 'partner';

  // Super Admin Navigation
  const superAdminItems = [
    {
      key: "dashboard",
      label: "Overview",
      icon: <DashboardIcon />,
      path: "/superadmin",
    },
    {
      key: "enterprises",
      label: "Enterprises",
      icon: <BusinessIcon />,
      path: "/superadmin/enterprises",
    },
    {
      key: "partners",
      label: "Partners",
      icon: <GroupsOutlinedIcon />,
      path: "/superadmin/partners",
    },
    {
      key: "analytics",
      label: "Analytics",
      icon: <AssessmentIcon />,
      path: "/superadmin/analytics",
    },
    {
      key: "billing",
      label: "Billing & collections",
      icon: <SummarizeIcon />,
      path: "/superadmin/billing",
    },
    {
      key: "support",
      label: "Customer support",
      icon: <SupportAgentOutlinedIcon />,
      path: "/superadmin/support",
    },
    {
      key: "operations",
      label: "Communications & health",
      icon: <CampaignOutlinedIcon />,
      path: "/superadmin/operations",
    },
  ];

  // Multi-cloud sidebar state
  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  const [onboarding, setOnboarding] = useState(null);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [enterpriseInfo, setEnterpriseInfo] = useState(null);
  const [enterpriseInfoLoading, setEnterpriseInfoLoading] = useState(true);
  const [cloudNavMeta, setCloudNavMeta] = useState({ total: 0, active: 0 });

  useEffect(() => {
    if (userRole === "super" || userRole === "superadmin" || isPartner) {
      setEnterpriseInfoLoading(false);
      setEnterpriseInfo(null);
      return;
    }

    setEnterpriseInfoLoading(true);
    EnterprisesAPI.getMyEnterprise()
      .then((res) => setEnterpriseInfo(res?.data?.data || null))
      .catch(() => setEnterpriseInfo(null))
      .finally(() => setEnterpriseInfoLoading(false));
  }, [userRole, isPartner]);

  useEffect(() => {
    if (userRole === "super" || userRole === "superadmin" || isPartner) return;
    listClouds()
      .then(res => {
        const list = Array.isArray(res) ? res : res?.data || [];
        const active = list.filter(c => String(c.status || "").toLowerCase() === "active").length;
        setCloudNavMeta({ total: list.length, active });
      })
      .catch(() => setCloudNavMeta({ total: 0, active: 0 }));
  }, [userRole, isPartner, location.pathname]);

  useEffect(() => {
    const enterpriseId = auth?.user?.enterprise_id;
    if (!isEnterpriseAdmin || !enterpriseId) {
      setOnboarding(null);
      return;
    }
    setOnboardingLoading(true);
    OnboardingAPI.progress()
      .then((res) => {
        const next = res?.data?.data || null;
        setOnboarding(next);
        const promptKey = `susha_onboarding_prompted_${enterpriseId}`;
        if (next && next.percentage < 100 && !localStorage.getItem(promptKey)) {
          setOnboardingOpen(true);
          localStorage.setItem(promptKey, String(Date.now()));
        }
      })
      .catch(() => setOnboarding(null))
      .finally(() => setOnboardingLoading(false));
  }, [auth?.user?.enterprise_id, isEnterpriseAdmin, location.pathname]);

  const enterpriseItems = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: <DashboardIcon />,
      path: ROUTES.ENTERPRISE,
    },
    {
      key: "assets",
      label: "Assets",
      icon: <Inventory2Icon />,
      path: ROUTES.ASSETS,
    },
    {
      key: "monitor",
      label: "Monitor",
      icon: <DevicesIcon />,
      path: ROUTES.MONITOR,
    },
    {
      key: "cmdb",
      label: "CMDB",
      icon: <AccountTreeIcon />,
      path: ROUTES.CMDB,
    },
    {
      key: "reports",
      label: "Reports",
      icon: <SummarizeIcon />,
      path: ROUTES.REPORTS,
      premium: true,
    },
    {
      key: "network_scan",
      label: "Agentless Scan",
      icon: <WifiIcon />,
      path: ROUTES.NETWORK_SCAN,
      premium: true,
    },
    {
      key: "connectors",
      label: "Credential Vault",
      icon: <VpnKeyIcon />,
      path: ROUTES.CONNECTORS,
      premium: true,
    },
    {
      key: "maintenance",
      label: "Maintenance",
      icon: <AssessmentIcon />,
      path: ROUTES.MAINTENANCE,
      premium: true,
    },
    {
      key: "cloud",
      label: "Cloud Connections",
      icon: <CloudIcon />,
      path: ROUTES.CLOUD,
      secondary: `${cloudNavMeta.active}/${cloudNavMeta.total} connected`,
    },
  ];

  const currentTier = String(enterpriseInfo?.user_status || "").toLowerCase();
  const hasPremiumAccess = enterpriseInfoLoading ? false : currentTier !== "free";

  const navigationItems = isSuperAdmin
    ? superAdminItems
    : enterpriseItems
        .filter((item) => {
          if (isEnterpriseAdmin) return true;
          if (!userRole || userRole === "super") return false;
          return item.key === "dashboard" || pageAccess.includes(item.key);
        })
      .map((item) => ({
          ...item,
          locked: Boolean(item.premium && !hasPremiumAccess),
        }));

  if (isPartner) {
    return (
      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#fafafa" }}>
        <TopBar onMenuClick={() => setMobileOpen(!mobileOpen)} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            mt: { xs: "56px", sm: "64px" },
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            overflowX: "hidden",
            bgcolor: "#fafafa",
            display: "flex",
            flexDirection: "column",
            minHeight: "100vh",
          }}
        >
          <Box sx={{ flex: 1 }}>{children || <Outlet />}</Box>
          <Footer />
        </Box>
      </Box>
    );
  }

  // Check if current path matches navigation item
  const isActive = (path) => location.pathname === path || (path === '/superadmin/enterprises' && location.pathname.startsWith('/superadmin/enterprises/'));

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: '#fafafa' }}>
      <TopBar onMenuClick={() => setMobileOpen(!mobileOpen)} />
      
      {/* Side Navigation */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          width: sidebarWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: sidebarWidth,
            boxSizing: 'border-box',
            border: 'none',
            bgcolor: '#ffffff',
            borderRight: '1px solid #e2e8f0',
            pt: { xs: '64px', sm: '70px' },
            transition: 'width 180ms ease-in-out',
          },
        }}
      >
        {/* Navigation Items */}
        <Box sx={{ px: sidebarCollapsed ? 1 : 2, height: '100%', display: 'flex', flexDirection: 'column', transition: 'padding 180ms ease-in-out' }}>
          <Box sx={{ minHeight: 46, display: 'flex', alignItems: 'center', justifyContent: sidebarCollapsed ? 'center' : 'space-between' }}>
            <Typography
              variant="caption"
              data-tour="workspace-nav"
              sx={{
                px: 1,
                color: '#64748b',
                fontWeight: 700,
                letterSpacing: 0.6,
                textTransform: 'uppercase',
                display: sidebarCollapsed ? 'none' : 'block',
              }}
            >
              {isSuperAdmin ? 'Susha · Platform owner' : 'Workspace'}
            </Typography>
            {!isMobile ? (
              <IconButton size="small" onClick={() => setSidebarCollapsed((current) => !current)} aria-label={sidebarCollapsed ? 'Expand navigation' : 'Collapse navigation'} title={sidebarCollapsed ? 'Expand navigation' : 'Collapse navigation'}>
                {sidebarCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
              </IconButton>
            ) : null}
          </Box>
          <List sx={{ p: 0 }}>
            {navigationItems.map((item) => {
              const active = isActive(item.path);
              return (
                <ListItemButton
                  key={item.key}
                  data-tour={
                    item.key === "dashboard"
                      ? "nav-dashboard"
                      : item.key === "assets"
                      ? "nav-assets"
                      : item.key === "devices"
                      ? "nav-monitor"
                      : item.key === "cloud"
                      ? "nav-cloud"
                      : item.key === "network-scan"
                      ? "nav-network-scan"
                      : undefined
                  }
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    py: 1.5,
                    px: sidebarCollapsed ? 1.5 : 2,
                    minHeight: 48,
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    transition: 'all 0.2s ease-in-out',
                    bgcolor: active ? '#f8fafc' : 'transparent',
                    border: active ? '1px solid #e2e8f0' : '1px solid transparent',
                    boxShadow: active ? 'inset 3px 0 0 #2563eb' : 'none',
                    '& .MuiListItemIcon-root': {
                      minWidth: sidebarCollapsed ? 0 : 36,
                      mr: sidebarCollapsed ? 0 : undefined,
                      color: active ? '#3b82f6' : '#64748b',
                      transition: 'color 0.2s ease-in-out'
                    },
                    '&:hover': {
                      bgcolor: active ? '#f1f5f9' : '#f8fafc',
                      '& .MuiListItemIcon-root': {
                        color: '#3b82f6'
                      },
                      '& .MuiListItemText-primary': {
                        color: '#1e293b'
                      }
                    }
                  }}
                  onClick={() => {
                    navigate(item.path);
                    if (isMobile) setMobileOpen(false);
                  }}
                  title={sidebarCollapsed ? item.label : undefined}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    secondary={item.locked ? null : item.secondary}
                    sx={{
                      display: sidebarCollapsed ? 'none' : 'block',
                      '& .MuiListItemText-primary': {
                        fontWeight: active ? 600 : 500,
                        fontSize: '0.9rem',
                        color: active ? '#1e293b' : '#475569',
                        transition: 'all 0.2s ease-in-out'
                      },
                      '& .MuiListItemText-secondary': {
                        fontSize: '0.72rem',
                        color: '#64748b',
                        mt: 0.2,
                      }
                    }}
                  />
                  {item.locked && !sidebarCollapsed ? <LockIcon fontSize="small" sx={{ color: '#cbd5e1', ml: 1 }} /> : null}
                </ListItemButton>
              );
            })}
          </List>
          
          {/* Enterprise Admin Actions at Bottom */}
          {isEnterpriseAdmin && (
            <Box sx={{ p: sidebarCollapsed ? 1 : 2, mt: 'auto', borderTop: '1px solid #e2e8f0' }}>
              {sidebarCollapsed ? <IconButton color="primary" title="Setup progress" aria-label="Setup progress" onClick={() => setOnboardingOpen(true)} sx={{ display: 'flex', mx: 'auto', mb: 1 }}><RocketLaunchOutlinedIcon /></IconButton> : <Button fullWidth variant="outlined" startIcon={<RocketLaunchOutlinedIcon />} onClick={() => setOnboardingOpen(true)} sx={{ mb: 1.25, py: .9, textTransform: 'none', justifyContent: 'flex-start', fontWeight: 700, borderColor: '#dbe7f5' }}>{onboarding ? `Setup progress · ${onboarding.percentage}%` : 'Setup progress'}</Button>}
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ px: 1, mb: 1, display: 'block' }}>
                {sidebarCollapsed ? '' : 'AGENT SETUP'}
              </Typography>
              {sidebarCollapsed ? <IconButton color="primary" title="Agent deployment" aria-label="Agent deployment" onClick={() => setAgentDialogOpen(true)} sx={{ display: 'flex', mx: 'auto' }}><DownloadIcon /></IconButton> : <Button fullWidth variant="contained" startIcon={<DownloadIcon />} onClick={() => setAgentDialogOpen(true)} sx={{ py: 1, textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#1d4ed8' } }}>Agent deployment</Button>}
              {currentTier === "free" && (
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => navigate(ROUTES.PRICING)}
                  sx={{
                    mt: 1.5,
                    py: 1,
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Upgrade to unlock premium modules
                </Button>
              )}
            </Box>
          )}
        </Box>
        
      </Drawer>

      {/* Agent Download Dialog */}
      <AgentDownloadDialog 
        open={agentDialogOpen}
        onClose={() => setAgentDialogOpen(false)}
      />
      <OnboardingDialog open={onboardingOpen} onClose={() => setOnboardingOpen(false)} progress={onboarding} loading={onboardingLoading} />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          mt: { xs: '56px', sm: '64px' },
          width: { 
            xs: '100%',
            md: `calc(100vw - ${sidebarWidth}px)`
          },
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'hidden',
          bgcolor: '#fafafa',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          transition: 'width 180ms ease-in-out',
        }}
      >
        <Box sx={{ flex: 1 }}>{children || <Outlet />}</Box>
        <Footer />
      </Box>
      <AppTour role={userRole} enterpriseId={auth?.user?.enterprise_id} />
    </Box>
  );
}
